import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  TextField,
  Card,
  CardContent,
  CardActions,
  AppBar,
  Toolbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { useSections } from "./SectionContext";

const sectionRoutes = {
  "Photo, Personal Details": "/PersonalDetail",
  "Websites, Portfolios, Profiles": "/WebsiteSection",
  Certifications: "/Certifications",
  Languages: "/Language",
  Accomplishments: "/AccomplishmentsSection",
  "Additional Information": "/AdditionalInfoForm",
  Software: "/SoftWare",
  References: "/Refferences",
  Volunteering: "/Volunteer",
  Interests: "/Interest",
};

const preWrittenExamples = [
  "Achieved [Result] by introducing [Software] for [Type] tasks.",
  "Achieved [Result] through effectively helping with [Task].",
  "Collaborated with team of [Number] in the development of [Project name].",
  "Resolved product issue through consumer testing.",
  "Used Microsoft Excel to develop inventory tracking spreadsheets.",
  "Achieved [Result] by completing [Task] with accuracy and efficiency.",
  "Supervised team of [Number] staff members.",
  "Documented and resolved [Issue] which led to [Results].",
];

const AccomplishmentsSection = () => {
  const [accomplishments, setAccomplishments] = useState("");
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [editIndex, setEditIndex] = useState(null);

  const navigate = useNavigate();
  const { sections } = useSections();
  const location = useLocation();

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: template, selectedTheme: theme } = location.state;
      if (template) setSelectedTemplate(template);
      if (theme) setSelectedTheme(theme);
    }
  }, [location.state]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("accomplishmentsList");
    if (saved) {
      setSavedAccomplishments(JSON.parse(saved));
    }
  }, []);

  const handleAddExample = (example) => {
    setAccomplishments((prev) => (prev ? prev + "\n" + example : example));
  };

  const handleChange = (e) => {
    setAccomplishments(e.target.value);
  };

  const handleSaveAndNext = () => {
    // Save accomplishment if text exists
    if (accomplishments.trim()) {
      let updatedList;
      if (editIndex !== null) {
        updatedList = [...savedAccomplishments];
        updatedList[editIndex] = accomplishments;
        setEditIndex(null);
      } else {
        updatedList = [...savedAccomplishments, accomplishments];
      }

      setSavedAccomplishments(updatedList);
      localStorage.setItem("accomplishmentsList", JSON.stringify(updatedList));
      setAccomplishments("");
    }

    // Always navigate to next section
    const currentSection = "Accomplishments";
    const currentIndex = sections.indexOf(currentSection);

    if (currentIndex !== -1 && currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      navigate(sectionRoutes[nextSection], {
        state: { selectedTemplate, selectedTheme },
      });
    } else {
      navigate("/mainpage", {
        state: { selectedTemplate, selectedTheme },
      });
    }
  };

  const handleEdit = (index) => {
    setAccomplishments(savedAccomplishments[index]);
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    const updatedList = savedAccomplishments.filter((_, i) => i !== index);
    setSavedAccomplishments(updatedList);
    localStorage.setItem("accomplishmentsList", JSON.stringify(updatedList));
  };

  return (
    <Box sx={{ backgroundColor: "#EFF7FF", minHeight: "100vh" }}>
      {/* Header with Back button */}
      <AppBar position="static" sx={{ backgroundColor: "#002B5B" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: "bold", letterSpacing: 0.5 }}>
            Accomplishments
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main content — responsive: column on mobile, row on desktop */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 4 },
          mt: { xs: 2, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: "1200px",
          mx: "auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Left side: Pre-written examples */}
        <Box
          sx={{
            flex: { xs: "unset", md: 1 },
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            mb={2}
            sx={{
              color: "#002B5B",
              fontWeight: "bold",
              borderBottom: "2px solid #0288D1",
              pb: 1,
            }}
          >
            Pre-written Examples
          </Typography>
          {preWrittenExamples.map((example, index) => (
            <Paper
              key={index}
              sx={{
                p: 1.5,
                mb: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                border: "1px solid #B3D9F7",
                borderRadius: 2,
                backgroundColor: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#E1F5FE",
                  borderColor: "#0288D1",
                  boxShadow: "0 2px 8px rgba(2, 136, 209, 0.2)",
                },
              }}
              onClick={() => handleAddExample(example)}
            >
              <Typography variant="body2" sx={{ flex: 1, pr: 1, color: "#0D4C8B" }}>
                {example}
              </Typography>
              <IconButton size="small" sx={{ color: "#0288D1", "&:hover": { color: "#002B5B" } }}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </Box>

        {/* Right side: textarea + saved list */}
        <Box
          sx={{
            flex: { xs: "unset", md: 2 },
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            mb={2}
            sx={{
              color: "#002B5B",
              fontWeight: "bold",
              borderBottom: "2px solid #0288D1",
              pb: 1,
            }}
          >
            Tell us about your accomplishments
          </Typography>
          <TextField
            multiline
            minRows={5}
            fullWidth
            value={accomplishments}
            onChange={handleChange}
            placeholder="Write your accomplishments here..."
            variant="outlined"
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#B3D9F7" },
                "&:hover fieldset": { borderColor: "#0288D1" },
                "&.Mui-focused fieldset": { borderColor: "#002B5B" },
              },
            }}
          />

          {/* Save & Next Button — always enabled */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveAndNext}
              sx={{
                px: 4,
                fontWeight: "bold",
                backgroundColor: "#002B5B",
                "&:hover": { backgroundColor: "#0288D1" },
                borderRadius: 2,
                boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
              }}
            >
              Save & Next
            </Button>
          </Box>

          {/* Saved accomplishments list */}
          <Box mt={4}>
            {savedAccomplishments.length > 0 && (
              <Typography
                variant="h6"
                mb={2}
                sx={{
                  color: "#002B5B",
                  fontWeight: "bold",
                  borderBottom: "2px solid #0288D1",
                  pb: 1,
                }}
              >
                Saved Accomplishments
              </Typography>
            )}
            {savedAccomplishments.map((item, index) => (
              <Card
                key={index}
                sx={{
                  mb: 2,
                  boxShadow: "0 2px 10px rgba(2, 136, 209, 0.15)",
                  border: "1px solid #B3D9F7",
                  borderLeft: "4px solid #002B5B",
                  borderRadius: 2,
                  "&:hover": { boxShadow: "0 4px 16px rgba(0, 43, 91, 0.2)" },
                }}
              >
                <CardContent>
                  <Typography variant="body2" whiteSpace="pre-line" sx={{ color: "#0D4C8B" }}>
                    {item}
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton
                    onClick={() => handleEdit(index)}
                    sx={{ color: "#0288D1", "&:hover": { color: "#002B5B" } }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(index)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AccomplishmentsSection;